import React, { ChangeEvent, createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useZooPassAppContext } from "../../Context/ZooPassAppContext";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { IZooPassAnswersContext, useZooPassAppAnswersContext, ZooPassAppAnswersContext } from "../../Context/ZooPassAnswersContext";
import { StepTitleContent } from "../PanelContents/StepTitleContent";
import ZooPassFetch from "../../Utilities/ZooPassFetch";
import {
    EmptyZooMemberDetails,
    FilteredAttributeDto,
    MembershipDto,
    ZooMemberDetails
} from "../../Data/TnewMemberDetails";
import ZooPassStepDataUtility from "../../Utilities/ZooPassStepDataUtility";
import ZooPassLocalStateUtility from "../../Utilities/ZooPassLocalStateUtility";
import { RenewalAnswerCard } from "../PanelContents/RenewalAnswerCard";
import {
    RenewalHrSegment,
    RenewalLoadingSegment,
    RenewalLoginSegment,
    RenewalStartANewPassSegment
} from "../PanelContents/RenewalSupportingSegments";
import { ZooPassOfferingItem } from "../../Data/ZooContrib";

enum RenewalStateFlags {
    None = 0,
    TranslationLoaded = 1 << 0,
    DataLoaded = 1 << 1,
    ProfileLoaded = 1 << 2,
    All = ~(~0 << 3)
}

export const Step2RenewalPanel: React.FC = () => {

    let {
        dictionary,
        textTitles,
        zooPassMembership,
        loading,
        memberShipDetails
    } = useZooPassAppContext();

    let {
        answersData,
        answersLoading,
        updateContext
    } = useZooPassAppAnswersContext();

    const [nonActionableRows, setNonActionableRows] = useState<ReactNode[]>([]);
    const [actionableRows, setActionableRows] = useState<ReactNode[]>([]);
    const [memberOffering, setMemberOffering] = useState<ZooPassOfferingItem | null>(null);

    /////////////////////////////////////////////////
    // Handle children selection
    const [showChildrenDropdown, setShowChildrenDropdown] = useState(false);
    const [selectedChildren, setSelectedChildren] = useState(1);
    const handleSelectedChildrenDropdownChange = (event: any) => {
        let value = event.target.value;
        setSelectedChildren(Number.parseInt(value));
    }
    const handleChildrenSelectedButton = () => {
        if (!answersLoading && answersData != null) {

            const offering = memberOffering;
            if (offering == null) return;
            const data = ZooPassStepDataUtility.SetStepDataFromRenewal(answersData, offering.lookup, selectedChildren as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9);
            data.previousStep = data.step;
            data.step = 3;
            ZooPassLocalStateUtility.SetLocalState(answersData);
            updateContext({ type: "setAnswers", value: answersData });
        }
    };
    // END: Handle children selection
    /////////////////////////////////////////////////


    /////////////////////////////////////////////////
    // Handle the load state
    const [stateFlags, setStateFlags] = useState<number>(RenewalStateFlags.None);
    useEffect(() => {
        //Set the loading flags ---------------------------------------
        let newFlags: number = RenewalStateFlags.None;
        if (!loading) newFlags ^= RenewalStateFlags.TranslationLoaded;
        if (!answersLoading) newFlags ^= RenewalStateFlags.DataLoaded;
        if (memberShipDetails && !answersLoading && answersData?.step == 2.01) newFlags ^= RenewalStateFlags.ProfileLoaded;
        setStateFlags(newFlags);

        //Load options ------------------------------------------------
        if (answersData?.step == 2.01 && memberShipDetails != null) {
            const actionAr: ReactNode[] = [];
            memberShipDetails.actionableMemberships.forEach((detail) => {
                actionAr.push(<RenewalAnswerCard detail={detail} onSelect={() => onItemSelected(detail)} key={detail.id} />);
            });
            setActionableRows(actionAr);

            const nonActionableAr: ReactNode[] = [];
            memberShipDetails.nonActionableMemberships.forEach((detail) => {
                nonActionableAr.push(<RenewalAnswerCard detail={detail} onSelect={() => onItemSelected(detail)} key={detail.id} />);
            });
            setNonActionableRows(nonActionableAr);

            if (answersData?.step == 2.01 && answersData?.step2RenewalPassId) {
                const offering = memberShipDetails.actionableMemberships.find(m => m.id == answersData.step2RenewalPassId);
                if (offering) {
                    answersData.step2RenewalPassId = null; // Reset the ID so we don't re-select it
                    ZooPassLocalStateUtility.SetLocalState(answersData);
                    updateContext({ type: "setAnswers", value: answersData });
                    onItemSelected(offering);
                }
            }
        }

    }, [loading, dictionary, answersLoading, memberShipDetails, answersData?.step]);

    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [showLogin, setShowLogin] = useState<boolean>(false);
    const [showOptions, setShowOptions] = useState<boolean>(false);
    useEffect(() => {
        setShowLoading(stateFlags != RenewalStateFlags.All);
        setShowLogin(stateFlags == RenewalStateFlags.All && !(memberShipDetails?.isLoggedIn ?? false));
        setShowOptions(stateFlags == RenewalStateFlags.All && (memberShipDetails?.isLoggedIn ?? false));
    }, [stateFlags]);
    // END: Handle the load state
    /////////////////////////////////////////////////

    const onItemSelected = (membership: MembershipDto) => {
        console.log("onItemSelected", membership);
        if (answersData == null) return;
        const offering = zooPassMembership.get(membership.membershipLevel.code);
        if (offering == undefined) {
            console.log("Offering for membership not found: ", membership);
            return;
        }



        const data = ZooPassStepDataUtility.SetStepDataFromRenewal(answersData, offering.lookup, offering.lookup.children ? 1 : 0);
        setMemberOffering(offering);

        if (!offering.lookup.children) {
            setShowChildrenDropdown(false);
            data.previousStep = data.step;
            data.step = 3;
            data.formData.memberID = membership.constituent.id;
            ZooPassLocalStateUtility.SetLocalState(data);
            updateContext({ type: "setAnswers", value: data });
        } else {
            setShowChildrenDropdown(true);
        }

    }

    const handleNewItemClick = () => onNewSelectedClick();

    const onNewSelectedClick = () => {
        if (answersData != null && !answersLoading) {
            answersData.step2Value = 0;
            answersData.previousStep = answersData.step;
            answersData.step = 3;
            ZooPassLocalStateUtility.SetLocalState(answersData);
            updateContext({ type: "setAnswers", value: answersData });
        }
    }

    const handleLoginClick = () => {
        console.log("Login Click");
        ZooPassFetch.redirectTNewLogin(window.baseApiUrl);
    };

    return !answersLoading && !loading &&
        textTitles !== null &&
        answersData !== null && (answersData.step == 2.01) && (
            <div className="container d-block step2-renewal-wrapper">
                {showLoading && <RenewalLoadingSegment />}
                {showLogin && <RenewalLoginSegment clickCallback={handleLoginClick} />}
                {showOptions && <>
                    {/*Options should be displayed + no rows*/}
                    {(actionableRows.length == 0 && nonActionableRows.length == 0) && <>
                        <StepTitleContent textTitle={textTitles.get("ZooPass:PageContent:02:Renew:No")} />
                        <RenewalStartANewPassSegment clickCallback={handleNewItemClick} />
                    </>}
                    {(actionableRows.length > 0 || nonActionableRows.length > 0) && <StepTitleContent textTitle={textTitles.get("ZooPass:PageContent:02:Renew:Yes")} />}
                    {actionableRows.length > 0 && <>
                        <div
                            className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-1 row-cols-sm-1 row-cols-1">
                            <div className="col">
                                {actionableRows}
                            </div>
                        </div>
                    </>}
                    {(actionableRows.length > 0 && nonActionableRows.length > 0) && <RenewalHrSegment />}
                    {nonActionableRows.length > 0 && <>
                        <div
                            className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-1 row-cols-sm-1 row-cols-1">
                            <div className="col">
                                {nonActionableRows}
                            </div>
                        </div>
                    </>}
                    {showChildrenDropdown && <>
                        <StepTitleContent textTitle={textTitles.get("ZooPass:PageContent:02:Renewal:ConfirmChildren")} />
                        <div
                            className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-1 row-cols-sm-1 row-cols-1">
                            <div className="col">
                                <div className="row align-items-center justify-content-center">
                                    <div className="col-5 col-sm-5 col-md-4">
                                        <Form.Select tabIndex={0} className="ms-auto"
                                            onSelect={handleSelectedChildrenDropdownChange}
                                            onChange={handleSelectedChildrenDropdownChange}
                                            aria-label={dictionary.get("ZooPass:Common:4Children:Title")}>
                                            <option value="1">1</option>
                                            <option value="2">2</option>
                                            <option value="3">3</option>
                                            <option value="4">4</option>
                                            <option value="5">5</option>
                                            <option value="6">6</option>
                                            <option value="7">7</option>
                                            <option value="8">8</option>
                                            <option value="9">9</option>
                                        </Form.Select>
                                    </div>
                                    <div className="col-5">
                                        <Button className="ms-3 zoopass--step4--continue-button w-100" tabIndex={0}
                                            onClick={handleChildrenSelectedButton}
                                            variant="secondary">{dictionary.get("ZooPass:Common:4Children:Cta")}</Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </>}
                    {(actionableRows.length != 0 || nonActionableRows.length != 0) && <>
                        <RenewalHrSegment />
                        <RenewalStartANewPassSegment clickCallback={handleNewItemClick} />
                    </>}
                </>}
            </div>

        );
};

